/**
 * ShareList - Admin component for managing shares
 *
 * Can be embedded in Reports tab or Admin view
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Share2, Search, RefreshCw, Loader2, Link2, XCircle, Ban, Eye, Calendar } from 'lucide-react';
import { shareAPI } from '../../api';
import { useFormat } from '../../i18n/format.js';

// labelKey resolved at render — never a translated string baked into a module-level
// constant, it would freeze at the language active on load.
const STATUS_BADGES = {
  ACTIVE: { labelKey: 'shares.status.active', className: 'bg-green-100 text-green-800' },
  EXPIRED: { labelKey: 'shares.status.expired', className: 'bg-slate-100 text-slate-800' },
  REVOKED: { labelKey: 'shares.status.revoked', className: 'bg-red-100 text-red-800' },
  LOCKED: { labelKey: 'shares.status.locked', className: 'bg-amber-100 text-amber-800' }
};

const SHARE_TYPE_KEYS = {
  report: 'shares.list.types.report',
  vehicle: 'shares.list.types.vehicle',
  claim: 'shares.list.types.claim'
};

export default function ShareList({ limit = 20 }) {
  const { t } = useTranslation();
  const { fmt } = useFormat();
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [revoking, setRevoking] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadShares();
  }, [filter]);

  const loadShares = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = { limit };
      if (filter !== 'all') {
        params.status = filter;
      }

      const response = await shareAPI.list(params);

      if (response.ok !== false) {
        setShares(Array.isArray(response) ? response : response.data || []);
      } else {
        setError(response.error?.message || t('common.states.loadError'));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (shareId) => {
    if (!window.confirm(t('shares.list.confirmRevoke'))) {
      return;
    }

    setRevoking(shareId);
    try {
      await shareAPI.revoke(shareId, 'Révoqué manuellement');
      loadShares();
    } catch (err) {
      console.error('Revoke failed:', err);
    } finally {
      setRevoking(null);
    }
  };

  const handleCopyLink = async (token) => {
    const url = shareAPI.getShareUrl(token);
    await navigator.clipboard.writeText(url);
  };

  // Filter shares by search query
  const filteredShares = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return shares;
    const query = searchQuery.toLowerCase();
    return shares.filter(share =>
      share.title?.toLowerCase().includes(query) ||
      share.createdByName?.toLowerCase().includes(query) ||
      share.shareType?.toLowerCase().includes(query)
    );
  }, [shares, searchQuery]);

  // Calculate stats
  const stats = useMemo(() => {
    const result = { total: 0, active: 0, expired: 0, revoked: 0, totalViews: 0 };
    shares.forEach(share => {
      result.total++;
      if (share.status === 'ACTIVE' && new Date(share.expiresAt) >= new Date()) {
        result.active++;
      } else if (share.status === 'REVOKED') {
        result.revoked++;
      } else {
        result.expired++;
      }
      result.totalViews += share.viewCount || 0;
    });
    return result;
  }, [shares]);

  if (loading && shares.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">{error}</p>
        <button
          onClick={loadShares}
          className="mt-2 text-blue-600 hover:underline"
        >
          {t('common.actions.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Card with Stats, Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Share2 className="w-6 h-6 text-blue-600" />
              {t('shares.list.title')}
            </h2>
            <p className="text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">{stats.total}</span> {t('shares.list.stats.count', { count: stats.total })}
              <span className="text-gray-300">|</span>
              <span className="text-green-600">{t('shares.list.stats.activeCount', { count: stats.active })}</span>
              {stats.expired > 0 && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="text-gray-500">{t('shares.list.stats.expiredCount', { count: stats.expired })}</span>
                </>
              )}
              {stats.revoked > 0 && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="text-red-600">{t('shares.list.stats.revokedCount', { count: stats.revoked })}</span>
                </>
              )}
              <span className="text-gray-300">|</span>
              <span className="text-blue-600 flex items-center gap-1">
                <Eye className="w-3 h-3" />
                {t('shares.list.stats.viewsCount', { count: stats.totalViews })}
              </span>
            </p>
          </div>
        </div>

        {/* Search and Refresh */}
        <div className="flex gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('shares.list.search.placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            onClick={loadShares}
            disabled={loading}
            className="px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg border border-gray-300 transition-colors"
            title={t('common.actions.refresh')}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
          <span className="text-sm text-gray-500">{t('shares.list.filterLabel')}</span>
          {['all', 'ACTIVE', 'EXPIRED', 'REVOKED'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                filter === status
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {status === 'all' ? t('shares.list.filterAll') : (STATUS_BADGES[status] ? t(STATUS_BADGES[status].labelKey) : status)}
            </button>
          ))}
          {(searchQuery || filter !== 'all') && (
            <button
              onClick={() => { setSearchQuery(''); setFilter('all'); }}
              className="ml-2 text-sm text-blue-600 hover:text-blue-700 underline"
            >
              {t('shares.list.reset')}
            </button>
          )}
          <div className="flex-1"></div>
          <span className="text-sm text-gray-600">
            {t('shares.list.displayedCount', { count: filteredShares.length })}
          </span>
        </div>
      </div>

      {/* Share list */}
      {filteredShares.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <Share2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {searchQuery || filter !== 'all' ? t('common.emptyState.search.title') : t('shares.list.empty.noSharesTitle')}
          </h3>
          <p className="text-gray-600">
            {searchQuery || filter !== 'all'
              ? t('shares.list.empty.filteredDescription')
              : t('shares.list.empty.defaultDescription')}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('shares.list.table.share')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('shares.list.table.type')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('shares.list.table.status')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('shares.list.table.views')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('shares.list.table.expires')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('shares.list.table.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredShares.map((share) => {
                const statusBadge = STATUS_BADGES[share.status] || STATUS_BADGES.ACTIVE;
                const isExpired = new Date(share.expiresAt) < new Date();
                const typeLabel = SHARE_TYPE_KEYS[share.shareType] ? t(SHARE_TYPE_KEYS[share.shareType]) : share.shareType;

                return (
                  <tr key={share.id} className="hover:bg-blue-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{share.title}</div>
                      <div className="text-sm text-gray-500">
                        {share.createdByName
                          ? t('shares.list.row.createdOnBy', { date: fmt.date(share.createdAt), author: share.createdByName })
                          : t('shares.list.row.createdOn', { date: fmt.date(share.createdAt) })}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">
                        {typeLabel}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.className}`}>
                        {t(statusBadge.labelKey)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1 text-gray-600">
                        <Eye className="w-4 h-4" />
                        {share.viewCount || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`flex items-center gap-1 ${isExpired ? 'text-red-600' : 'text-gray-600'}`}>
                        <Calendar className="w-4 h-4" />
                        {fmt.date(share.expiresAt)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {share.status === 'ACTIVE' && !isExpired && (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleCopyLink(share.tokenSha256?.slice(0, 43))}
                            className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                            title={t('shares.success.copyLinkTitle')}
                          >
                            <Link2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRevoke(share.id)}
                            disabled={revoking === share.id}
                            className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title={t('shares.list.actions.revoke')}
                          >
                            {revoking === share.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Ban className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
